import '../../styles/LandingPage/FinancialReporting.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function FinancialReporting() {
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = contentRef.current?.querySelectorAll('.fr-animate');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="fr-detail-section" ref={contentRef}>
      <div className="fr-detail-container">
        <Link to="/" className="fr-back-link fr-animate">
          <i className="ti ti-arrow-left" />
          Back to Research Pillars
        </Link>

        <div className="fr-detail-header fr-animate">
          <div className="fr-detail-label">Pillar 01</div>
          <div className="fr-detail-icon">
            <i className="ti ti-file-description" />
          </div>
          <h1>Financial Reporting & Capital Frameworks</h1>
          <p className="fr-detail-subtitle">
            Navigating the complexities of IFRS 17, embedded value metrics, and modern capital reporting standards for African insurers.
          </p>
        </div>

        <div className="fr-content">
          <div className="fr-block fr-animate">
            <h2>IFRS 17 Implementation</h2>
            <p>
              The transition to IFRS 17 represents one of the most significant changes in insurance accounting history. 
              Our research examines the practical challenges African insurers face in implementing this standard, 
              including the measurement of the Contractual Service Margin (CSM), risk adjustment methodologies, 
              and the impact on financial statement presentation. We analyse how different product categories 
              — life, non-life, and reinsurance — require tailored approaches under the General Measurement Model (GMM), 
              Variable Fee Approach (VFA), and Premium Allocation Approach (PAA).
            </p>
            <div className="fr-highlight">
              <i className="ti ti-bulb" />
              <span>Key Insight: CSM volatility under VFA can be 3-5x higher than traditional embedded value approaches for participating products.</span>
            </div>
          </div>

          <div className="fr-block fr-animate">
            <h2>CSM Behaviour & Profit Emergence</h2>
            <p>
              Understanding how the Contractual Service Margin unlocks over time is critical for forecasting profitability 
              and managing stakeholder expectations. Our research models CSM release patterns across various product types, 
              examining how assumptions about lapses, mortality, and expense experience affect the timing and magnitude 
              of profit recognition. We explore the interaction between CSM and loss components, and how insurers can 
              optimise their product design to achieve smoother earnings profiles.
            </p>
          </div>

          <div className="fr-block fr-animate">
            <h2>Risk Adjustment Methodologies</h2>
            <p>
              The risk adjustment under IFRS 17 captures the compensation an insurer requires for bearing uncertainty 
              about the amount and timing of insurance cash flows. We investigate the cost of capital approach versus 
              confidence level techniques, with particular attention to the 75th percentile calibration requirement. 
              Our work includes developing proxy models for risk adjustment that balance computational efficiency 
              with regulatory compliance, especially relevant for smaller African insurers with limited actuarial resources.
            </p>
          </div>

          <div className="fr-block fr-animate">
            <h2>Embedded Value & Market Consistent Value</h2>
            <p>
              While IFRS 17 provides a new accounting framework, embedded value (EV) and market consistent embedded value (MCEV) 
              remain essential tools for investor communication and strategic decision-making. Our research bridges the gap 
              between these metrics, showing how IFRS 17 balance sheet items relate to traditional EV components. 
              We examine the appropriateness of different discount rate curves for African markets, where liquidity 
              premiums and sovereign risk spreads require careful calibration.
            </p>
          </div>

          <div className="fr-block fr-animate">
            <h2>Current Research Outputs</h2>
            <div className="fr-outputs">
              <div className="fr-output-card">
                <i className="ti ti-file-analytics" />
                <h4>IFRS 17 Transition Guide for African Insurers</h4>
                <span>Technical Paper · 2025</span>
              </div>
              <div className="fr-output-card">
                <i className="ti ti-chart-bar" />
                <h4>CSM Sensitivity Analysis Framework</h4>
                <span>Model & Documentation · 2025</span>
              </div>
              <div className="fr-output-card">
                <i className="ti ti-calculator" />
                <h4>Risk Adjustment Proxy Model</h4>
                <span>Open-source Tool · 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
